import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { api } from '../services/api';

interface PermissionsResponse {
  role: string;
  permissions: string[];
}

export const usePermissions = () => {
  const { user } = useAuthStore();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user) {
        console.log('🔍 [usePermissions] No user, skipping permissions fetch');
        setPermissions([]);
        setLoading(false);
        return;
      }

      console.log('🔍 [usePermissions] Fetching permissions for user:', user.email, 'Role:', user.role);

      try {
        const response = await api.get<{ data: PermissionsResponse }>('/users/my-permissions');
        console.log('📋 [usePermissions] Received response:', response.data);
        console.log('📋 [usePermissions] Permissions data:', response.data.data);
        console.log('📋 [usePermissions] Permissions count:', response.data.data?.permissions?.length);
        console.log('📋 [usePermissions] Has users.view:', response.data.data?.permissions?.includes('users.view'));
        setPermissions(response.data.data?.permissions || []);
      } catch (error) {
        console.error('❌ [usePermissions] Failed to fetch permissions:', error);
        setPermissions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [user]);

  // Check if user has a specific permission
  const hasPermission = (permission: string): boolean => {
    return permissions?.includes(permission) || false;
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (perms: string[]): boolean => {
    return perms.some(perm => permissions?.includes(perm)) || false;
  };

  // Check if user has all of the specified permissions
  const hasAllPermissions = (perms: string[]): boolean => {
    return perms.every(perm => permissions?.includes(perm)) || false;
  };

  // Check if user has a specific role
  const hasRole = (role: string): boolean => {
    return user?.role === role;
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles: string[]): boolean => {
    return roles.includes(user?.role || '');
  };

  return {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
  };
};
